import { useEffect, useReducer } from 'react';

import useDropdown from '../usedropdown';
import { LINE_OF_BUSINESS } from '../../../services/auditsearchservice';

import reducer from './reducer';
import * as ACTIONS from './actions';

const noop = () => {};
const toFakeEvent = x => ({
  target: { value: x },
  preventDefault: noop,
  stopPropagation: noop
});

export const INPUT_TYPES = {
  CHECKBOX: 'checkbox',
  TEXT: 'text',
  DATE: 'date'
};

const auditSearchInitialState = {
  isLoading: false,
  statusMessage: '',
  inputs: {
    text: {
      mrn: '',
      accountNumber: ''
    },
    checkbox: {
      showOnlyNewHCC: false,
      showOnlyOnHold: false
    }
  },
  selectedCoders: [],
  numberOfEncounters: 0
};

const useAuditSearch = (service) => {
  const [state, dispatch] = useReducer(reducer, auditSearchInitialState);
  const [contractsDropdownState, contractsDropdownActions] = useDropdown();
  const [facilitiesDropdownState, facilitiesDropdownActions] = useDropdown();
  const [codersDropdownState, codersDropdownActions] = useDropdown();

  const withDispatchUnary = fn => x => dispatch(fn(x))

  const changeNewHCCOnlyValue = withDispatchUnary(ACTIONS.changeInputValue(
    INPUT_TYPES.CHECKBOX,
    'showOnlyNewHCC'
  ));

  const changeOnlyOnHoldValue = withDispatchUnary(ACTIONS.changeInputValue(
    INPUT_TYPES.CHECKBOX,
    'showOnlyOnHold'
  ));

  const changeMrnValue = withDispatchUnary(ACTIONS.changeInputValue(
    INPUT_TYPES.TEXT,
    'mrn'
  ));

  const changeAccountNumberValue = withDispatchUnary(ACTIONS.changeInputValue(
    INPUT_TYPES.TEXT,
    'accountNumber'
  ));

  const getCodersForContract = contractId => {
    dispatch(ACTIONS.getCodersRequest());

    return service.getCodersForContract(contractId)
      .then(result => {
        codersDropdownActions.setItems(result.coders);
        dispatch(ACTIONS.getCodersSuccess());
      })
      .catch(e => dispatch(ACTIONS.getCodersFailure(e.toString())));
  };

  const setContractAndUpdateCoders = e => {
    const contractId = e.target.value;

    return getCodersForContract(contractId)
      .then(() => {
        contractsDropdownActions.setSelectedItem(toFakeEvent(contractId));
      });
  };

  const selectCoder = coderId => {
    const isSameId = x => x.value === coderId;
    const coder = codersDropdownState.items.filter(isSameId)[0];

    dispatch(ACTIONS.selectCoder(coder));
  };

  const removeCoder = withDispatchUnary(ACTIONS.removeCoder);

  const getEncounterCount = dto => {
    dispatch(ACTIONS.searchRequest());

    return service.getEncounterCount(dto)
      .then(result => {
        dispatch(ACTIONS.searchSuccess(result));
      })
      .catch(e => dispatch(ACTIONS.searchFailure(e.toString())));
  };

  const beginWork = doRedirect => {
    dispatch(ACTIONS.beginWorkRequest());

    return service.beginWork()
      .then(result => {
        dispatch(ACTIONS.beginWorkSuccess());
        doRedirect(service.serviceRoutes.redirectToDetails(result));
      })
      .catch(e => dispatch(ACTIONS.beginWorkFailure(e.toString())));
  };

  const boundActions = {
    changeNewHCCOnlyValue,
    changeOnlyOnHoldValue,
    changeMrnValue,
    changeAccountNumberValue,
    setSelectedContract: setContractAndUpdateCoders,
    setSelectedFacility: facilitiesDropdownActions.setSelectedItem,
    getCodersForContract,
    selectCoder,
    removeCoder,
    getEncounterCount,
    beginWork
  };

  // init
  useEffect(() => {
    dispatch(ACTIONS.initRequest());

    service.init(LINE_OF_BUSINESS.RAM)
      .then(result => {
        contractsDropdownActions.setItems(result.contracts);
        contractsDropdownActions.setSelectedItem(
          toFakeEvent(result.selectedContract.value)
        );

        facilitiesDropdownActions.setItems(result.facilities);
        facilitiesDropdownActions.setSelectedItem(
          toFakeEvent(result.selectedFacility.value)
        );

        codersDropdownActions.setItems(result.auditCoders);

        dispatch(ACTIONS.initSuccess(result));
      })
      .catch(e => dispatch(ACTIONS.initFailure(e.toString())));
  }, []);

  const isPageLoading = (
    state.isLoading
  );

  const getStatusMessage = () => {
    const hasMessage = s => s.statusMessage.length > 0;

    return state.statusMessage;
  };

  return [
    {
      ...state,
      isLoading: isPageLoading,
      statusMessage: getStatusMessage(),
      contracts: { ...contractsDropdownState },
      facilities: { ...facilitiesDropdownState },
      auditCoders: { ...codersDropdownState }
    },
    boundActions
  ];
};

export default useAuditSearch;
